import { getApiBaseUrl } from "../config/runtimeConfig";
import { invalidateLookup } from "../lookups/lookupsApi";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export type InvoiceValidationStatus =
  | "PendingValidation"
  | "Validated"
  | "Rejected";

/** Estado de processamento de um documento de despesa submetido para extração AI. */
export type DocumentProcessingStatus =
  | "Pending"
  | "Processing"
  | "Ready"
  | "Failed";

/** Resultado/estado de um upload de documento de despesa (upload → OCR/AI → fatura). */
export interface InvoiceExtraction {
  documentId: number;
  fileName: string;
  processingStatus: DocumentProcessingStatus;
  processingError?: string | null;
  /** "Ready" sem fatura = descartado — a classificação diz porquê (ex.: "Duplicate"). */
  classification?: string | null;
  classificationReason?: string | null;
  /** Duplicado: ids de TODAS as faturas já existentes com o mesmo conteúdo, por ordem de criação. */
  duplicateOfInvoiceIds?: number[] | null;
  /** Id da fatura criada quando o processamento termina (Ready). */
  invoiceId?: number | null;
  /** Fatura com os dados extraídos, disponível quando processingStatus === "Ready". */
  invoice?: Invoice | null;
}

/** Uma fatia da repartição de uma fatura por obra. */
export interface InvoiceAllocation {
  id: number;
  workId?: number | null;
  workCode?: string | null;
  workName?: string | null;
  /** Nome cru da obra lido do documento, quando ainda não casou com uma obra. */
  suggestedWorkName?: string | null;
  amount: number;
  expenseCategoryId?: number | null;
  expenseCategoryName?: string | null;
  description?: string | null;
  sortOrder: number;
  /** Linha de custo de estrutura (sem obra): imputa sem gerar custo de obra. */
  isStructural: boolean;
  /** Origem da atribuição: "fingerprint" | "memory-exact" | "memory-similar" | "llm" | "manual" | "category"; null = por atribuir. */
  assignmentSourceCode?: string | null;
  assignmentSourceName?: string | null;
  /** Veredicto 👍/👎 dado a esta linha ("ok"/"not-ok"); null = ainda sem feedback. */
  feedbackTypeCode?: string | null;
  feedbackErrorDescription?: string | null;
  feedbackCorrectedWorkId?: number | null;
  feedbackAt?: string | null;
}

/** Fatura de despesa a nível de empresa (menu Financeiro → Faturas). */
/** Um comprovativo de pagamento da linha (ficheiro + montante lido). */
export interface PaymentProofItem {
  id: number;
  fileName: string;
  amount?: number | null;
  uploadedAt: string;
}

export interface Invoice {
  id: number;
  /** Empresa do grupo a que a linha pertence. */
  companyId?: number | null;
  companyName?: string | null;
  workId?: number | null;
  workCode?: string | null;
  workName?: string | null;
  documentNumber?: string | null;
  /** A linha é uma nota de crédito — fica fora do Resumo Financeiro. */
  isCreditNote?: boolean;
  /** Despesa anulada por uma nota de crédito — fica fora do Resumo Financeiro. */
  isAnnulledByCreditNote?: boolean;
  /** Excluída do Resumo Financeiro por decisão do utilizador. */
  excludeFromSummary?: boolean;
  /** Data de pagamento combinada com o fornecedor (fora da regra da casa). */
  plannedPaymentDate?: string | null;
  /** Tarefa "Pagar …" criada com a data combinada. */
  paymentTaskItemId?: number | null;
  /** Referência da NC que anulou esta linha (ex.: "NC 4/10125"). */
  creditNoteReference?: string | null;
  documentDate?: string | null;
  description?: string | null;
  supplierName?: string | null;
  supplierTaxNumber?: string | null;
  /** Confiança da leitura automática, de 0 a 1. Ausente nas faturas criadas à mão. */
  confidence?: number | null;
  /** Fase do ciclo documental, derivada: `prevista` | `com-auto` | `com-fatura` | `paga`. */
  lifecyclePhase: string;
  /** O valor previsto pelo utilizador, quando esta despesa resultou de uma fusão fatura→previsão. */
  forecastedAmount?: number | null;
  /** Previsões que PODEM ser esta fatura — sugestões, quem decide é o utilizador. */
  forecastSuggestions?: ForecastSuggestion[];
  matchedSupplierId?: number | null;
  matchedSupplierName?: string | null;
  expenseCategoryId?: number | null;
  expenseCategoryCode?: string | null;
  expenseCategoryName?: string | null;
  isStructural?: boolean | null;
  /** Sub-tipo de texto livre — só nas despesas de categoria sem obra. */
  subType?: string | null;
  baseAmount?: number | null;
  vatAmount?: number | null;
  totalAmount?: number | null;
  currencyCode: string;
  approvalStatusId?: number | null;
  approvalStatusCode: string;
  approvalStatusName: string;
  documentId?: number | null;
  documentFileName?: string | null;
  hasDocument: boolean;
  /**
   * O documento anexado é um auto de medição e não a fatura. Reconhecido na leitura, corrigível à
   * mão — a ranhura da fatura fica livre para a fatura do fornecedor, que chega depois.
   */
  documentIsAuto: boolean;
  /** Nº de autos/anexos extra da fatura (EntityDocuments com secção "auto"). */
  autoDocumentsCount: number;
  /** Montante liquidado (lido do comprovativo ou corrigido à mão); menor que o total = parcial. */
  paidAmount?: number | null;
  hasPaymentProof: boolean;
  paymentProofFileName?: string | null;
  paymentProofUploadedAt?: string | null;
  /** Só presente na resposta a um upload sem valor lido: a razão, para mostrar ao utilizador. */
  paymentProofReadNote?: string | null;
  /** Comprovativos da linha (uma linha pode ter vários; o valor pago é a soma dos montantes). */
  paymentProofs: PaymentProofItem[];
  validationStatus: InvoiceValidationStatus;
  /** Validada pela leitura (QR certificado + NIF casado + total coerente), sem gesto humano. */
  autoValidated?: boolean;

  /** Repartição por obras (Auto partilhado). Vazio = obra única ou estrutura. */
  allocations: InvoiceAllocation[];
  /** Verdadeiro quando a despesa está repartida por obras. */
  isSplit: boolean;
  /** Nº de obras: 1 (obra única), 0 (estrutura), ou nº de fatias distintas (repartida). */
  workCount: number;
  /** Σ dos custos imputados a partir desta fatura; null/0 = por imputar. */
  imputedAmount?: number | null;
  imputedCostCount: number;

  /** A distribuição foi reaplicada do fingerprint de um documento já revisto (possível duplicado). */
  reappliedFromPreviousReview: boolean;

  /** Origem do documento: null/"manual" (upload) ou "email" (importado da caixa de faturas). */
  sourceChannel?: string | null;
  sourceSender?: string | null;
  sourceSubject?: string | null;
  sourceReceivedAt?: string | null;

  created: string;
  lastModified: string;
}

/**
 * Totais do conjunto filtrado INTEIRO, somados no servidor antes de paginar. Não somar no
 * cliente: a listagem traz no máximo `pageSize` linhas e a soma das visíveis ficaria a menos
 * assim que o filtro apanhasse mais faturas do que uma página.
 */
export interface InvoiceTotals {
  /** Soma s/IVA. */
  baseTotal: number;
  vatTotal: number;
  /** Soma c/IVA. */
  grandTotal: number;
  /** Nº de faturas somadas (igual ao totalCount da listagem). */
  count: number;
}

export interface InvoiceListResult {
  items: Invoice[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  totals: InvoiceTotals;
}

/** Estado de um email processado pela importação de faturas (caixa faturas@aja2.pt). */
export type EmailImportStatus =
  | "Imported"
  | "NoAttachments"
  | "PartiallyFailed"
  | "Failed";

/** Uma linha do registo de importações por email (painel Financeiro → Importações por email). */
export interface EmailImport {
  id: number;
  /** Id da mensagem no Gmail (chave de idempotência; útil para cruzar com a caixa). */
  gmailMessageId: string;
  from?: string | null;
  subject?: string | null;
  receivedAt: string;
  processedAt: string;
  /** Nº total de anexos com nome encontrados no email (elegíveis ou não). */
  attachmentCount: number;
  /** Nº de anexos ignorados pelo filtro (tipo não aceite ou demasiado pequenos). */
  skippedAttachmentCount: number;
  /** Nomes dos anexos ignorados (separados por " · "); null quando não há ou ainda não foram revistos. */
  skippedAttachmentNames: string | null;
  /** Os anexos ignorados já foram dados como vistos: o email deixa de contar no número do menu. */
  skippedDismissed: boolean;
  /** Nº de anexos efetivamente importados como documentos de fatura. */
  importedCount: number;
  status: EmailImportStatus | string;
  error?: string | null;
  /** Verdadeiro quando o corpo do email foi guardado e pode ser visto. */
  hasEmail: boolean;
  created: string;
  lastModified: string;
}

export interface EmailImportListResult {
  items: EmailImport[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
}

export interface EmailImportFilters {
  status?: string;
  /** true = só emails com anexos ignorados por rever (os que o menu conta). */
  toReview?: boolean | null;
  search?: string;
  pageNumber?: number;
  pageSize?: number;
}

/** Uma linha de repartição enviada ao criar/atualizar. `id` preenchido = atualizar a existente. */
export interface InvoiceAllocationInput {
  id?: number | null;
  workId?: number | null;
  suggestedWorkName?: string | null;
  amount: number;
  expenseCategoryId?: number | null;
  description?: string | null;
  /** Linha de custo de estrutura (sem obra) — exclui workId. */
  isStructural?: boolean;
}

export interface InvoicePayload {
  /** Empresa do grupo; omitido usa a empresa por omissão. */
  companyId?: number | null;
  workId?: number | null;
  expenseCategoryId?: number | null;
  /** Sub-tipo de texto livre; o backend só o guarda nas categorias sem obra. */
  subType?: string | null;
  supplierName?: string | null;
  supplierTaxNumber?: string | null;
  matchedSupplierId?: number | null;
  documentNumber?: string | null;
  documentDate?: string | null;
  description?: string | null;
  baseAmount?: number | null;
  vatAmount?: number | null;
  totalAmount?: number | null;
  /** Montante liquidado; corrige à mão o que foi lido do comprovativo. */
  paidAmount?: number | null;
  currencyCode?: string | null;
  /** Repartição por obras. null/omitido = não mexer; lista (mesmo vazia) = conjunto autoritário. */
  allocations?: InvoiceAllocationInput[] | null;
}

/**
 * Valores oferecidos nos filtros de coluna. Vêm dos lançamentos que existem, e não das tabelas de
 * Fornecedores/Obras: essas listam entidades que nunca aparecem em faturas e, pior, não incluem as
 * faturas cujo emitente não foi casado com um fornecedor — filtrar por elas dava zero resultados.
 */
export interface InvoiceFilterOptions {
  /** Entidades tal como aparecem na coluna (fornecedor casado ou nome lido do documento). */
  entities: string[];
  /** Sub-tipos (texto livre) já usados em despesas — alimentam a auto-sugestão do campo. */
  subTypes: string[];
  /** Obras com pelo menos uma fatura, no cabeçalho ou numa repartição. */
  works: { id: number; code?: string | null; name: string }[];
}

/** Opções dos filtros de coluna. `revenue` = lado da receita; omitido/false = despesa. */
export function getInvoiceFilterOptions(
  revenue = false,
): Promise<InvoiceFilterOptions> {
  return request<InvoiceFilterOptions>(
    `/api/invoices/filter-options${revenue ? "?revenue=true" : ""}`,
  );
}

export interface InvoiceFilters {
  /** Empresa do grupo; omitido/null = todas. */
  companyId?: number | null;
  workId?: number | null;
  /** Nome da entidade tal como é mostrado na coluna (ver `InvoiceFilterOptions`). */
  entity?: string | null;
  structural?: boolean | null;
  /** Sub-tipo da despesa de estrutura; `sem-sub-tipo` devolve as que ainda não têm nenhum. */
  subType?: string | null;
  /** Fase do ciclo documental: `prevista` | `com-auto` | `com-fatura` | `paga`. */
  lifecycle?: string | null;
  expenseCategoryId?: number | null;
  approvalStatusCode?: string | null;
  /** true = só as linhas "fora do Resumo do ano"; false = só as que contam; omitido = todas. */
  excludedFromSummary?: boolean | null;
  /** true = só por validar; false = só validadas; omitido = todas. */
  pendingOnly?: boolean | null;
  /** Intervalo de datas do documento (`YYYY-MM-DD`), inclusivo nas duas pontas. */
  from?: string | null;
  to?: string | null;
  /** Intervalo da data de ENTRADA no sistema (`YYYY-MM-DD`), inclusivo — não a data do documento. */
  createdFrom?: string | null;
  createdTo?: string | null;
  /** `"entrada"` ordena pelo que chegou primeiro à casa; omitido mantém a data do documento. */
  sort?: string | null;
  /** Ordem crescente; por omissão mostra o mais recente primeiro. */
  sortAscending?: boolean | null;
  /** Filtro "contém" sobre o nº do documento. */
  documentNumber?: string | null;
  /** Filtro "contém" sobre o descritivo. */
  description?: string | null;
  /** Valor s/IVA, com a mesma aritmética de fallback do total do rodapé. */
  net?: number | null;
  /** Valor c/IVA, idem. */
  gross?: number | null;
  /**
   * Coluna Documentos: `with-document` | `without-document` | `with-payment-proof` |
   * `without-payment-proof` | `split` (autos repartidos por várias obras).
   */
  documents?: string | null;
  search?: string | null;
  pageNumber?: number;
  pageSize?: number;
}

export interface ExpenseCategoryOption {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
  parentId?: number | null;
  isStructural?: boolean | null;
}

export interface WorkOption {
  id: number;
  code: string;
  name: string;
  /** Código do estado da obra ("encerrada", "em_execucao", …) — o endpoint já o devolve. */
  statusCode?: string | null;
}

export interface SupplierOption {
  id: number;
  name: string;
  taxId?: string | null;
}

export interface ApprovalStatusOption {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function downloadFile(
  path: string,
  fallbackFileName: string,
): Promise<void> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition");
  let fileName = fallbackFileName;

  if (disposition) {
    const match = disposition.match(
      /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i,
    );
    if (match?.[1]) {
      fileName = decodeURIComponent(match[1]);
    }
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** O ficheiro em si, para o viewer o mostrar dentro da app em vez de o descarregar. */
async function fetchFile(path: string): Promise<Blob> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return response.blob();
}

export function getInvoices(
  filters: InvoiceFilters = {},
): Promise<InvoiceListResult> {
  const params = new URLSearchParams();
  const set = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  };
  set("companyId", filters.companyId);
  set("workId", filters.workId);
  set("entity", filters.entity);
  set("structural", filters.structural);
  set("subType", filters.subType);
  set("lifecycle", filters.lifecycle);
  set("expenseCategoryId", filters.expenseCategoryId);
  set("approvalStatusCode", filters.approvalStatusCode);
  set("excludedFromSummary", filters.excludedFromSummary);
  set("pendingOnly", filters.pendingOnly);
  set("from", filters.from);
  set("to", filters.to);
  set("documentNumber", filters.documentNumber);
  set("description", filters.description);
  set("createdFrom", filters.createdFrom);
  set("createdTo", filters.createdTo);
  set("sort", filters.sort);
  set("sortAscending", filters.sortAscending);
  set("net", filters.net);
  set("gross", filters.gross);
  set("documents", filters.documents);
  set("search", filters.search);
  set("pageNumber", filters.pageNumber);
  set("pageSize", filters.pageSize);

  const query = params.toString();
  return request<InvoiceListResult>(
    `/api/invoices${query ? `?${query}` : ""}`,
  );
}

/**
 * Exporta a listagem de despesas filtrada em Excel/CSV — o conjunto filtrado inteiro, não só a
 * página visível. Nas faturas repartidas sai uma linha por obra; com filtro de obra, só a fatia
 * dessa obra (a mesma aritmética do total do rodapé). Com `ids`, exporta apenas essas faturas
 * (as selecionadas na grelha).
 */
export function exportInvoices(
  filters: InvoiceFilters,
  format: "excel" | "csv",
  ids?: number[],
): Promise<void> {
  const params = new URLSearchParams();
  const set = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  };
  set("companyId", filters.companyId);
  set("workId", filters.workId);
  set("entity", filters.entity);
  set("structural", filters.structural);
  set("subType", filters.subType);
  set("lifecycle", filters.lifecycle);
  set("expenseCategoryId", filters.expenseCategoryId);
  set("approvalStatusCode", filters.approvalStatusCode);
  set("excludedFromSummary", filters.excludedFromSummary);
  set("pendingOnly", filters.pendingOnly);
  set("from", filters.from);
  set("to", filters.to);
  set("createdFrom", filters.createdFrom);
  set("createdTo", filters.createdTo);
  set("documentNumber", filters.documentNumber);
  set("description", filters.description);
  set("net", filters.net);
  set("gross", filters.gross);
  set("documents", filters.documents);
  set("search", filters.search);
  ids?.forEach((id) => params.append("ids", String(id)));

  const query = params.toString();
  return downloadFile(
    `/api/invoices/export/${format}${query ? `?${query}` : ""}`,
    `faturas-despesas.${format === "excel" ? "xlsx" : "csv"}`,
  );
}

/** O espelho de {@link exportInvoices} para o lado da receita (entidade = cliente). */
export function exportRevenueInvoices(
  filters: RevenueInvoiceFilters,
  format: "excel" | "csv",
  ids?: number[],
): Promise<void> {
  const params = new URLSearchParams();
  params.append("revenue", "true");
  const set = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  };
  set("companyId", filters.companyId);
  set("workId", filters.workId);
  set("entity", filters.entity);
  set("from", filters.from);
  set("to", filters.to);
  set("createdFrom", filters.createdFrom);
  set("createdTo", filters.createdTo);
  set("documentNumber", filters.documentNumber);
  set("net", filters.net);
  set("gross", filters.gross);
  set("documents", filters.documents);
  set("search", filters.search);
  // `false` é significativo aqui (= só validadas), por isso passa no filtro do `set`.
  set("pendingOnly", filters.pendingOnly);
  set("approvalStatusCode", filters.approvalStatusCode);
  set("excludedFromSummary", filters.excludedFromSummary);
  ids?.forEach((id) => params.append("ids", String(id)));

  return downloadFile(
    `/api/invoices/export/${format}?${params.toString()}`,
    `faturas-receitas.${format === "excel" ? "xlsx" : "csv"}`,
  );
}

export function getEmailImports(
  filters: EmailImportFilters = {},
): Promise<EmailImportListResult> {
  const params = new URLSearchParams();
  const set = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  };
  set("status", filters.status);
  set("toReview", filters.toReview);
  set("search", filters.search);
  set("pageNumber", filters.pageNumber);
  set("pageSize", filters.pageSize);

  const query = params.toString();
  return request<EmailImportListResult>(
    `/api/invoices/email-imports${query ? `?${query}` : ""}`,
  );
}

export interface ReprocessEmailInvoicesResult {
  /** Documentos importados por email re-enfileirados para reprocessamento. */
  queued: number;
  /** Documentos ignorados por já estarem validados (não se reprocessam). */
  skippedValidated: number;
}

/** Volta a correr a extração/classificação sobre os documentos importados por email (sem duplicar). */
export function reprocessEmailInvoices(): Promise<ReprocessEmailInvoicesResult> {
  return request<ReprocessEmailInvoicesResult>("/api/invoices/reprocess-email", {
    method: "POST",
  });
}

export interface ResetEmailImportsResult {
  documentsDeleted: number;
  messagesDeleted: number;
  /** Documentos ignorados por já estarem validados (não são apagados). */
  skippedValidated: number;
}

/** Apaga os dados criados pela importação por email (para reimportar do zero). Não toca no Gmail. */
export function resetEmailImports(): Promise<ResetEmailImportsResult> {
  return request<ResetEmailImportsResult>("/api/invoices/reset-email-imports", {
    method: "POST",
  });
}

/** Corpo (HTML) guardado de um email importado, para pré-visualização num iframe isolado. */
export async function getEmailImportBody(id: number): Promise<string> {
  const response = await fetch(
    `${apiBaseUrl}/api/invoices/email-imports/${id}/email`,
    { credentials: "include" },
  );
  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
  return response.text();
}

/** Um documento importado por email que o classificador descartou como "não parece fatura". */
export interface DiscardedDocument {
  id: number;
  fileName: string;
  sourceSender?: string | null;
  sourceSubject?: string | null;
  sourceReceivedAt?: string | null;
  /** Pontuação (0–100) que suportou o descarte. */
  classificationScore?: number | null;
  /** Motivo curto e explicável (ex.: "sem QR; sem nº; sem NIF; total=0"). */
  classificationReason?: string | null;
  /** `not-invoice` (descartado pelo classificador) ou `failed` (processamento falhou). */
  kind: string;
  /** O porquê, por extenso: a razão do classificador ou o erro de processamento. */
  motive?: string | null;
  created: string;
}

export interface DiscardedDocumentListResult {
  items: DiscardedDocument[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
}

export interface DiscardedDocumentFilters {
  search?: string;
  pageNumber?: number;
  pageSize?: number;
}

/** Lista os documentos importados por email descartados como "não parece fatura" (recuperáveis). */
export function getDiscardedDocuments(
  filters: DiscardedDocumentFilters = {},
): Promise<DiscardedDocumentListResult> {
  const params = new URLSearchParams();
  const set = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  };
  set("search", filters.search);
  set("pageNumber", filters.pageNumber);
  set("pageSize", filters.pageSize);

  const query = params.toString();
  return request<DiscardedDocumentListResult>(
    `/api/invoices/discarded${query ? `?${query}` : ""}`,
  );
}

export interface ReclassifyDocumentResult {
  documentId: number;
  queued: boolean;
}

/** Recupera um documento descartado: força "fatura" e reenfileira para criar a fatura "por validar". */
export function reclassifyDocument(
  id: number,
): Promise<ReclassifyDocumentResult> {
  return request<ReclassifyDocumentResult>(
    `/api/invoices/documents/${id}/reclassify`,
    { method: "POST" },
  );
}

/** Dispensa um documento descartado: foi revisto e não é fatura. Sai da lista; o ficheiro fica guardado. */
export function dismissDiscardedDocument(id: number): Promise<void> {
  return request<void>(`/api/invoices/documents/${id}/dismiss`, { method: "POST" });
}

/** Dá como vistos os anexos ignorados de todos os emails por rever. Devolve quantos emails. */
export function dismissAllSkippedAttachments(): Promise<number> {
  return request<number>("/api/invoices/email-imports/dismiss-skipped", { method: "POST" });
}

/** Dá como vistos os anexos ignorados de um email importado (deixa de contar no menu). */
export function dismissSkippedAttachments(messageId: number): Promise<void> {
  return request<void>(`/api/invoices/email-imports/${messageId}/dismiss-skipped`, {
    method: "POST",
  });
}

/** Veredicto 👍/👎 persistido sobre uma linha de repartição (snapshot da sugestão avaliada). */
export interface AllocationFeedbackResult {
  id: number;
  workFinancialEntryId: number;
  allocationId?: number | null;
  documentId?: number | null;
  feedbackTypeCode: string;
  errorDescription?: string | null;
  suggestedWorkName?: string | null;
  proposedWorkId?: number | null;
  proposedWorkName?: string | null;
  correctedWorkId?: number | null;
  correctedWorkName?: string | null;
  amount: number;
  supplierName?: string | null;
  supplierTaxNumber?: string | null;
  feedbackAt: string;
}

export interface SubmitAllocationFeedbackPayload {
  allocationId: number;
  /** "ok" (👍) ou "not-ok" (👎). */
  feedbackTypeCode: "ok" | "not-ok";
  /** Obrigatória no 👎 — é o texto que alimenta o treino da IA. */
  errorDescription?: string | null;
  correctedWorkId?: number | null;
}

/** Regista (upsert) o veredicto 👍/👎 de uma linha de repartição; regravar substitui o anterior. */
export function submitAllocationFeedback(
  payload: SubmitAllocationFeedbackPayload,
): Promise<AllocationFeedbackResult> {
  return request<AllocationFeedbackResult>("/api/invoices/allocations/feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export interface RevenueInvoiceFilters {
  /** Empresa do grupo; omitido/null = todas. */
  companyId?: number | null;
  workId?: number | null;
  /** Nome da entidade — do lado da receita é o cliente a quem emitimos. */
  entity?: string | null;
  /** Intervalo de datas do documento (`YYYY-MM-DD`), inclusivo nas duas pontas. */
  from?: string | null;
  to?: string | null;
  /** Intervalo da data de ENTRADA no sistema (`YYYY-MM-DD`), inclusivo — não a data do documento. */
  createdFrom?: string | null;
  createdTo?: string | null;
  /** `"entrada"` ordena pelo que chegou primeiro à casa; omitido mantém a data do documento. */
  sort?: string | null;
  /** Ordem crescente; por omissão mostra o mais recente primeiro. */
  sortAscending?: boolean | null;
  /** Filtro "contém" sobre o nº do documento. */
  documentNumber?: string | null;
  /** Valor s/IVA, com a mesma aritmética de fallback do total do rodapé. */
  net?: number | null;
  /** Valor c/IVA, idem. */
  gross?: number | null;
  /** Coluna Doc.: `with-document` | `without-document`. */
  documents?: string | null;
  search?: string;
  /** `undefined`/`null` = todas, `true` = só por validar, `false` = só validadas. */
  pendingOnly?: boolean | null;
  /** Código do Estado de Pagamento (a mesma tabela das despesas); `sem-estado` = sem estado atribuído. */
  approvalStatusCode?: string | null;
  /** true = só as linhas "fora do Resumo do ano"; false = só as que contam; omitido = todas. */
  excludedFromSummary?: boolean | null;
  pageNumber?: number;
  pageSize?: number;
}

/** Faturas de RECEITA (emitidas pela AJA2) — o espelho de getInvoices para o lado da receita. */
export function getRevenueInvoices(
  filters: RevenueInvoiceFilters = {},
): Promise<InvoiceListResult> {
  const params = new URLSearchParams();
  const set = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  };
  set("companyId", filters.companyId);
  set("workId", filters.workId);
  set("entity", filters.entity);
  set("from", filters.from);
  set("to", filters.to);
  set("createdFrom", filters.createdFrom);
  set("createdTo", filters.createdTo);
  set("sort", filters.sort);
  set("sortAscending", filters.sortAscending);
  set("documentNumber", filters.documentNumber);
  set("net", filters.net);
  set("gross", filters.gross);
  set("documents", filters.documents);
  set("search", filters.search);
  // `false` é significativo aqui (= só validadas), por isso passa no filtro do `set`.
  set("pendingOnly", filters.pendingOnly);
  set("approvalStatusCode", filters.approvalStatusCode);
  set("excludedFromSummary", filters.excludedFromSummary);
  set("pageNumber", filters.pageNumber);
  set("pageSize", filters.pageSize);

  const query = params.toString();
  return request<InvoiceListResult>(
    `/api/invoices/revenue${query ? `?${query}` : ""}`,
  );
}

export function createInvoice(payload: InvoicePayload): Promise<Invoice> {
  return request<Invoice>("/api/invoices", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Sobe um documento de despesa (PDF/imagem) para extração via Azure Document Intelligence + AI.
 * Devolve imediatamente o estado (Pending); a fatura é criada de forma assíncrona — acompanhar
 * com {@link getInvoiceDocumentStatus} até ProcessingStatus === "Ready".
 */
export function extractInvoiceFromDocument(file: File): Promise<InvoiceExtraction> {
  const data = new FormData();
  data.append("file", file);
  return request<InvoiceExtraction>("/api/invoices/extract", {
    method: "POST",
    body: data,
  });
}

/** Estado de processamento de um documento submetido para extração (polling). */
export function getInvoiceDocumentStatus(
  documentId: number,
): Promise<InvoiceExtraction> {
  return request<InvoiceExtraction>(`/api/invoices/documents/${documentId}`);
}

export function updateInvoice(
  id: number,
  payload: InvoicePayload,
): Promise<Invoice> {
  return request<Invoice>(`/api/invoices/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function deleteInvoice(id: number): Promise<void> {
  return request<void>(`/api/invoices/${id}`, { method: "DELETE" });
}

/** O que o apagar em grupo deixou feito: quantas caíram, quantas falharam e o primeiro erro. */
export interface DeleteInvoicesResult {
  deleted: number;
  failed: number;
  error?: string | null;
}

/** Apaga as faturas selecionadas de uma vez, com a mesma limpeza do apagar individual. */
/** Valida a seleção inteira num gesto; devolve quantas mudaram (as já validadas não contam). */
export function validateInvoices(ids: number[]): Promise<{ validated: number }> {
  return request<{ validated: number }>("/api/invoices/batch-validate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
}

/** O que está à espera de alguém, para o menu mostrar sem abrir ecrãs. */
export interface AttentionSummary {
  pendingValidation: number;
  /** Linhas da Importação por decidir: descartados por recuperar/dispensar + emails com anexos ignorados por rever. */
  importsToReview: number;
  /** Parte de `importsToReview`: documentos descartados por decidir. */
  discardedToReview: number;
  /** Parte de `importsToReview`: emails com anexos ignorados por rever. */
  skippedToReview: number;
}

export function getAttentionSummary(): Promise<AttentionSummary> {
  return request<AttentionSummary>("/api/invoices/attention-summary");
}

export function deleteInvoices(ids: number[]): Promise<DeleteInvoicesResult> {
  return request<DeleteInvoicesResult>("/api/invoices/batch-delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
}

export function setInvoiceApprovalStatus(
  id: number,
  statusCode: string,
): Promise<Invoice> {
  return request<Invoice>(`/api/invoices/${id}/approval-status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ statusCode }),
  });
}

/** Imputa a fatura ao ledger de custos (um WorkCost por obra, se repartida). */
export function imputeInvoiceCost(id: number): Promise<Invoice> {
  return request<Invoice>(`/api/invoices/${id}/impute-cost`, { method: "POST" });
}

/** Anula a imputação: remove os custos com origem nesta fatura. */
export function removeInvoiceCostImputation(id: number): Promise<Invoice> {
  return request<Invoice>(`/api/invoices/${id}/impute-cost`, { method: "DELETE" });
}

export function uploadInvoiceDocument(
  id: number,
  file: File,
): Promise<Invoice> {
  const data = new FormData();
  data.append("file", file);
  return request<Invoice>(`/api/invoices/${id}/document`, {
    method: "POST",
    body: data,
  });
}

/** Diz se o documento anexado é a fatura ou um auto de medição. */
export function setInvoiceDocumentKind(
  id: number,
  isAuto: boolean,
): Promise<Invoice> {
  return request<Invoice>(
    `/api/invoices/${id}/document/kind?isAuto=${isAuto}`,
    { method: "POST" },
  );
}

/** Marca/desmarca a linha como nota de crédito (fica fora do Resumo Financeiro). */
export function setInvoiceCreditNote(
  id: number,
  isCreditNote: boolean,
): Promise<Invoice> {
  return request<Invoice>(`/api/invoices/${id}/credit-note`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ isCreditNote }),
  });
}

/** Marca/desmarca a despesa como anulada por uma NC, com a referência dela. */
/** Exclui (ou volta a incluir) a linha no Resumo Financeiro; a linha continua viva em todo o lado. */
export function setInvoiceSummaryExclusion(
  id: number,
  excluded: boolean,
): Promise<Invoice> {
  return request<Invoice>(`/api/invoices/${id}/summary-exclusion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ excluded }),
  });
}

/** Marca (ou desmarca) a seleção inteira como "fora do Resumo" num gesto. */
export function setInvoicesSummaryExclusion(
  ids: number[],
  excluded: boolean,
): Promise<{ updated: number }> {
  return request<{ updated: number }>("/api/invoices/batch-summary-exclusion", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, excluded }),
  });
}

export interface ScheduleInvoicePaymentResult {
  updated: number;
  skipped: number;
  tasks: { id: number; title: string; dueDate: string | null }[];
}

/** "Pagar noutra data": data combinada para a seleção (null = volta ao pagamento normal). */
export function scheduleInvoicePayment(
  ids: number[],
  paymentDate: string | null,
): Promise<ScheduleInvoicePaymentResult> {
  return request<ScheduleInvoicePaymentResult>("/api/invoices/batch-payment-date", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, paymentDate }),
  });
}

export function setInvoiceAnnulment(
  id: number,
  isAnnulled: boolean,
  creditNoteReference?: string | null,
): Promise<Invoice> {
  return request<Invoice>(`/api/invoices/${id}/credit-note-annulment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ isAnnulled, creditNoteReference }),
  });
}

/** A fatura completa a despesa prevista: uma linha só, com o palpite guardado e reversível. */
export function completeForecast(
  invoiceEntryId: number,
  forecastEntryId: number,
): Promise<Invoice> {
  return request<Invoice>(
    `/api/invoices/${invoiceEntryId}/complete-forecast/${forecastEntryId}`,
    { method: "POST" },
  );
}

/** Desfaz a fusão: a previsão volta ao palpite e a fatura renasce como linha própria. */
export function unmergeForecast(entryId: number): Promise<Invoice> {
  return request<Invoice>(`/api/invoices/${entryId}/unmerge-forecast`, {
    method: "POST",
  });
}

export function downloadInvoiceDocument(invoice: Invoice): Promise<void> {
  return downloadFile(
    `/api/invoices/${invoice.id}/document`,
    invoice.documentFileName ?? `fatura-${invoice.id}`,
  );
}

export function fetchInvoiceDocument(invoice: Invoice): Promise<Blob> {
  return fetchFile(`/api/invoices/${invoice.id}/document`);
}

/** O ficheiro de um documento importado que não gerou fatura (descartado, duplicado ou falhado). */
export function fetchFinancialDocument(documentId: number): Promise<Blob> {
  return fetchFile(`/api/invoices/documents/${documentId}/file`);
}

/** Remove a fatura (documento principal) da linha e apaga o ficheiro; os autos e as faturas
 * adicionais ficam. */
export function removeInvoiceDocument(id: number): Promise<Invoice> {
  return request<Invoice>(`/api/invoices/${id}/document`, {
    method: "DELETE",
  });
}

/** Remove UM comprovativo (pelo seu id); o valor pago da linha é recalculado como a soma dos que ficam. */
export function removeInvoicePaymentProof(proofId: number): Promise<Invoice> {
  return request<Invoice>(`/api/invoices/payment-proofs/${proofId}`, {
    method: "DELETE",
  });
}

/** Acrescenta um comprovativo à linha (não substitui os anteriores). */
export function uploadInvoicePaymentProof(
  id: number,
  file: File,
): Promise<Invoice> {
  const data = new FormData();
  data.append("file", file);
  return request<Invoice>(`/api/invoices/${id}/payment-proof`, {
    method: "POST",
    body: data,
  });
}

/** Descarrega o comprovativo mais recente da linha (compatibilidade). */
export function downloadInvoicePaymentProof(invoice: Invoice): Promise<void> {
  return downloadFile(
    `/api/invoices/${invoice.id}/payment-proof`,
    invoice.paymentProofFileName ?? `comprovativo-${invoice.id}`,
  );
}

/** O comprovativo mais recente da linha, para o viewer. */
export function fetchInvoicePaymentProof(invoice: Invoice): Promise<Blob> {
  return fetchFile(`/api/invoices/${invoice.id}/payment-proof`);
}

/** UM comprovativo pelo seu id, para o viewer. */
export function fetchPaymentProof(proof: PaymentProofItem): Promise<Blob> {
  return fetchFile(`/api/invoices/payment-proofs/${proof.id}`);
}

export function getExpenseCategories(): Promise<ExpenseCategoryOption[]> {
  return request<ExpenseCategoryOption[]>("/api/expense-categories");
}

export function getWorks(): Promise<WorkOption[]> {
  return request<WorkOption[]>("/api/works");
}

export function getSuppliers(): Promise<SupplierOption[]> {
  return request<SupplierOption[]>("/api/suppliers");
}

export interface CreateSupplierPayload {
  name: string;
  taxId?: string | null;
}

/** Cria um fornecedor na tabela de fornecedores (a partir de nome/NIF livres). */
export function createSupplier(
  payload: CreateSupplierPayload,
): Promise<SupplierOption> {
  return request<SupplierOption>("/api/suppliers", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).then((result) => {
    invalidateLookup("suppliers");
    return result;
  });
}

export function getApprovalStatuses(): Promise<ApprovalStatusOption[]> {
  return request<ApprovalStatusOption[]>(
    "/api/works/financial-entries/approval-statuses",
  );
}

/** Uma fatura por pagar dentro de um conjunto proposto por um comprovativo. */
export interface ForecastSuggestion {
  forecastEntryId: number;
  /** 0–1; a UI traduz em palavras (muito parecida / parecida / pode ser). */
  score: number;
  description?: string | null;
  supplierName?: string | null;
  forecastAmount?: number | null;
  /** A linha sugerida já tem a fatura: juntar-lhe este auto põe-no nos autos e anexos dela. */
  hasInvoice?: boolean;
  documentNumber?: string | null;
  documentDate?: string | null;
  /** Obra(s) da linha sugerida — é o que distingue dois autos do mesmo sub-empreiteiro. */
  works?: ForecastSuggestionWork[];
}

export interface ForecastSuggestionWork {
  workId?: number | null;
  workName?: string | null;
  suggestedWorkName?: string | null;
  isStructural?: boolean;
}

export interface PaymentProofInvoice {
  invoiceId: number;
  supplierName: string | null;
  documentNumber: string | null;
  documentDate: string | null;
  outstanding: number;
}

/** Conjunto de faturas cuja soma fecha o valor do comprovativo. */
export interface PaymentProofMatch {
  invoices: PaymentProofInvoice[];
  total: number;
  reason: string;
}

/** Linha por beneficiário, nos comprovativos que pagam vários (ficheiros de lote). */
export interface PaymentProofLine {
  beneficiary: string | null;
  amount: number | null;
  reference: string | null;
}

/** Proposta devolvida ao ler um comprovativo. Nada foi aplicado ainda. */
export interface PaymentProofProposal {
  blobName: string;
  fileName: string;
  proofAmount: number | null;
  paidOn: string | null;
  lines: PaymentProofLine[];
  matches: PaymentProofMatch[];
  payable: PaymentProofInvoice[];
  summary: string;
}

export interface ApplyPaymentProofResult {
  settledCount: number;
  partiallySettledCount: number;
  unapplied: number;
}

/** Sobe um comprovativo que pode cobrir várias faturas e recebe a proposta. Não altera nada. */
export function matchPaymentProof(file: File): Promise<PaymentProofProposal> {
  const data = new FormData();
  data.append("file", file);
  return request<PaymentProofProposal>("/api/invoices/payment-proof/match", {
    method: "POST",
    body: data,
  });
}

/** Aplica o comprovativo às faturas confirmadas, pela ordem indicada. */
export function applyPaymentProof(input: {
  blobName: string;
  fileName: string;
  amount: number;
  invoiceIds: number[];
}): Promise<ApplyPaymentProofResult> {
  return request<ApplyPaymentProofResult>("/api/invoices/payment-proof/apply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}
